import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, ChevronRight, FileText, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { formatCents } from '@/components/repairs';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardHeader, CardTitle, Checkbox, Skeleton, Textarea } from '@/components/ui/primitives';
import { Pagination } from '@/components/ui/table';
import { api, ApiError, errorMessage } from '@/lib/api';
import { formatQuantity, type LegalSummary, type Paginated, type PortalRepairDetail, type PortalRepairItem } from '@/lib/types';

/** Bestätigte Rechtstexte bei der Freigabe */
export type Accept = Partial<Record<'terms' | 'privacy' | 'withdrawal', boolean>>;
import { cn, formatDate, formatDateTime } from '@/lib/utils';

function statusVariant(r: { status: string; quotePending: boolean }) {
  if (r.quotePending) return 'warning' as const;
  if (r.status === 'READY_FOR_PICKUP') return 'success' as const;
  if (r.status === 'PICKED_UP' || r.status === 'CANCELLED') return 'secondary' as const;
  return 'default' as const;
}

export function PortalRepairsPage() {
  const [state, setState] = useState<'open' | 'done' | 'all'>('open');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['portal', 'repairs', state, page],
    queryFn: () => api<Paginated<PortalRepairItem>>(`/portal/repairs?state=${state}&page=${page}&pageSize=20`),
  });
  return (
    <>
      <PageHeader title="Meine Reparaturen" description="Verfolgen Sie den Stand Ihrer Geräte in unserer Werkstatt." />
      <div className="mb-3 flex gap-1">
        {(
          [
            ['open', 'In Bearbeitung'],
            ['done', 'Abgeschlossen'],
            ['all', 'Alle'],
          ] as const
        ).map(([v, l]) => (
          <button
            key={v}
            onClick={() => {
              setState(v);
              setPage(1);
            }}
            className={cn('rounded-full border px-3 py-1 text-sm', state === v ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}
          >
            {l}
          </button>
        ))}
      </div>
      <Card>
        {isLoading && <Skeleton className="m-4 h-24" />}
        {data?.items.length === 0 && <div className="px-6 py-14 text-center text-sm text-muted-foreground">Keine Reparaturen.</div>}
        <ul className="divide-y">
          {data?.items.map((r) => (
            <li key={r.id}>
              <Link to={`/portal/repairs/${r.id}`} className="flex items-center gap-3 px-4 py-3.5 hover:bg-muted/40">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground">{r.number}</span>
                    <Badge variant={statusVariant(r)}>{r.statusLabel}</Badge>
                    {r.quotePending && <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Ihre Freigabe wird benötigt</span>}
                  </div>
                  <p className="mt-0.5 truncate font-medium">{r.device}</p>
                  <p className="text-xs text-muted-foreground">Abgegeben am {formatDate(r.receivedAt)}</p>
                </div>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
        {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
      </Card>
    </>
  );
}

export function PortalRepairPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const key = ['portal', 'repair', id];
  const { data: r, error } = useQuery({ queryKey: key, queryFn: () => api<PortalRepairDetail>(`/portal/repairs/${id}`) });
  const decide = useMutation({
    mutationFn: ({ approved, note, accept }: { approved: boolean; note: string; accept: Accept }) =>
      api(`/portal/repairs/${id}/quote/decision`, { method: 'POST', body: { approved, note: note || undefined, ...(approved ? { accept } : {}) } }),
    onSuccess: (_d, v) => {
      toast.success(v.approved ? 'Vielen Dank – wir setzen die Reparatur fort.' : 'Ihre Ablehnung wurde übermittelt.');
      qc.invalidateQueries({ queryKey: ['portal'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (error instanceof ApiError && error.status === 404) return <Alert>Diese Reparatur wurde nicht gefunden.</Alert>;
  if (!r) return <Skeleton className="h-96" />;
  return (
    <>
      <Link to="/portal/repairs" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Meine Reparaturen
      </Link>
      <RepairDetailContent
        r={r}
        decide={(approved, note, accept) => decide.mutate({ approved, note, accept })}
        deciding={decide.isPending ? (decide.variables?.approved ?? null) : null}
        photoSrc={(photoId) => `/api/portal/repairs/${r.id}/photos/${photoId}`}
        documentLink={(type) => ({ href: `/api/portal/repairs/${r.id}/documents/${type}` })}
      />
    </>
  );
}

/** Reparatur in Kundensicht – im Kundenportal und beim Zugang ohne Konto (Link mit Einmalcode). */
export function RepairDetailContent({
  r,
  decide,
  deciding,
  photoSrc,
  documentLink,
}: {
  r: PortalRepairDetail;
  decide: (approved: boolean, note: string, accept: Accept) => void;
  /** true/false = Freigabe bzw. Ablehnung läuft */
  deciding: boolean | null;
  photoSrc: (photoId: string) => string | undefined;
  documentLink: (type: string) => { href?: string; onClick?: () => void };
}) {
  const [note, setNote] = useState('');
  const [accept, setAccept] = useState<Accept>({});
  const [confirmDialog, confirm] = useConfirm();
  const openQuote = r.quotes.find((q) => q.status === 'SENT');
  // Rechtstexte für die Bestätigung (öffentlich, je Mandant)
  const { data: legal } = useQuery({ queryKey: ['public', 'legal'], queryFn: () => api<LegalSummary>('/public/legal'), enabled: Boolean(openQuote && r.canDecideQuote) });
  const allAccepted = Boolean(legal?.complete && legal.items.every((d) => accept[d.kind] === true));

  return (
    <>
      {confirmDialog}
      <div className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm text-muted-foreground">{r.number}</span>
          <Badge variant={statusVariant(r)}>{r.statusLabel}</Badge>
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">{r.device}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Abgegeben am {formatDateTime(r.receivedAt)}
          {r.serialNumber && ` · SN ${r.serialNumber}`}
          {r.estimatedCompletionAt && !r.readyAt && ` · voraussichtlich fertig am ${formatDate(r.estimatedCompletionAt)}`}
        </p>
      </div>

      {r.status === 'READY_FOR_PICKUP' && (
        <Alert variant="success" className="mb-4">
          Ihr Gerät ist abholbereit{r.outcome ? ` (${r.outcome})` : ''}. Bitte bringen Sie Ihren Annahmeschein oder die Reparaturnummer mit.
        </Alert>
      )}

      {openQuote && (
        <Card className="mb-4 border-amber-400/60">
          <CardHeader>
            <CardTitle>Kostenvoranschlag – Ihre Entscheidung</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <table className="w-full text-sm">
              <tbody>
                {openQuote.items.map((i, idx) => (
                  <tr key={idx} className="border-b last:border-0">
                    <td className="py-1.5">{i.description}</td>
                    <td className="whitespace-nowrap py-1.5 pl-3 text-right text-muted-foreground tabular-nums">
                      {formatQuantity(i.quantity, i.unit)} × {formatCents(i.unitPriceCents)}
                    </td>
                    <td className="py-1.5 pl-3 text-right tabular-nums">{formatCents(Math.round(i.quantity * i.unitPriceCents))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-right text-sm">
              <p className="text-muted-foreground">
                Netto {formatCents(openQuote.totals.net)} · USt. {openQuote.taxRate} % {formatCents(openQuote.totals.tax)}
              </p>
              <p className="text-lg font-semibold">Gesamt {formatCents(openQuote.totals.gross)}</p>
              {openQuote.validUntil && <p className="text-xs text-muted-foreground">gültig bis {formatDate(openQuote.validUntil)}</p>}
            </div>
            {openQuote.note && <p className="rounded-md bg-muted/50 p-3 text-sm">{openQuote.note}</p>}
            {r.canDecideQuote ? (
              <>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional: Ihre Nachricht an uns" aria-label="Nachricht" />
                {legal && !legal.complete ? (
                  <Alert>Die Online-Freigabe ist derzeit nicht möglich. Bitte geben Sie den Kostenvoranschlag telefonisch oder per E-Mail frei. Ablehnen können Sie hier.</Alert>
                ) : (
                  <fieldset className="grid gap-2 rounded-md border p-3 text-sm">
                    <legend className="px-1 text-xs text-muted-foreground">Für die Freigabe bitte bestätigen</legend>
                    {legal?.items.map((d) => (
                      <label key={d.kind} className="flex items-start gap-2">
                        <Checkbox checked={accept[d.kind] === true} onCheckedChange={(v) => setAccept((a) => ({ ...a, [d.kind]: v === true }))} className="mt-0.5" aria-label={d.title} />
                        <span>
                          {d.consentLabel}{' '}
                          <a href={d.url ?? `/legal/${d.kind}`} target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-2 hover:underline">
                            {d.title} öffnen
                          </a>
                        </span>
                      </label>
                    ))}
                  </fieldset>
                )}
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <Button
                    variant="outline"
                    loading={deciding === false}
                    onClick={async () => {
                      if (await confirm({ title: 'Kostenvoranschlag ablehnen?', description: 'Wir melden uns zum weiteren Vorgehen (z. B. unreparierte Rückgabe).', confirmLabel: 'Ablehnen', destructive: true })) decide(false, note, {});
                    }}
                  >
                    <XCircle /> Ablehnen
                  </Button>
                  <Button
                    loading={deciding === true}
                    disabled={!allAccepted}
                    title={allAccepted ? undefined : 'Bitte zuerst die Bestätigungen oben anhaken'}
                    onClick={async () => {
                      if (await confirm({ title: `Reparatur für ${formatCents(openQuote.totals.gross)} beauftragen?`, confirmLabel: 'Kostenpflichtig freigeben' })) decide(true, note, accept);
                    }}
                  >
                    <CheckCircle2 /> Freigeben
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Die Freigabe erfolgt durch eine berechtigte Person Ihres Unternehmens.</p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="flex min-w-0 flex-col gap-4">
          <Card>
            <CardContent className="grid gap-2 pt-5 text-sm">
              <p className="text-xs text-muted-foreground">Ihre Fehlerbeschreibung</p>
              <p className="whitespace-pre-wrap">{r.faultDescription}</p>
              {r.accessories.length > 0 && <p className="text-xs text-muted-foreground">Mitgegebenes Zubehör: {r.accessories.join(', ')}</p>}
            </CardContent>
          </Card>
          {(r.report || r.steps.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Durchgeführte Arbeiten</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2 text-sm">
                {r.report && <p className="whitespace-pre-wrap">{r.report}</p>}
                {r.steps.map((s, i) => (
                  <p key={i} className="flex gap-2">
                    <span className="shrink-0 text-xs text-muted-foreground">{formatDate(s.createdAt)}</span>
                    <span>
                      {s.text}
                      {s.testResult && <Badge variant={s.testResult === 'PASSED' ? 'success' : 'destructive'} className="ml-2">{s.testResult === 'PASSED' ? 'bestanden' : 'nicht bestanden'}</Badge>}
                    </span>
                  </p>
                ))}
              </CardContent>
            </Card>
          )}
          {r.photos.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Fotos</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {r.photos.map((p) => (
                  <a key={p.id} href={photoSrc(p.id)} target="_blank" rel="noreferrer" className="overflow-hidden rounded-md border">
                    {photoSrc(p.id) ? <img src={photoSrc(p.id)} alt={p.name} className="aspect-square w-full object-cover" loading="lazy" /> : <Skeleton className="aspect-square w-full" />}
                  </a>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
        <aside className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Verlauf</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="flex flex-col gap-3 border-l pl-4">
                {r.history.map((h, i) => (
                  <li key={i} className="relative text-sm">
                    <span className="absolute -left-[21px] top-1 size-2.5 rounded-full border-2 border-background bg-primary" />
                    <p>{h.label}</p>
                    <p className="text-xs text-muted-foreground">{formatDateTime(h.createdAt)}</p>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Belege</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-1.5">
              {r.documents.map((d) => {
                const l = documentLink(d.type);
                return l.href ? (
                  <a key={d.type} href={l.href} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm hover:underline">
                    <FileText className="size-4 text-muted-foreground" /> {d.title}
                  </a>
                ) : (
                  <button key={d.type} type="button" onClick={l.onClick} className="flex items-center gap-2 text-left text-sm hover:underline">
                    <FileText className="size-4 text-muted-foreground" /> {d.title}
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </aside>
      </div>
    </>
  );
}
